import { useCallback } from 'react';
import { 
  LocationData,
  Floor,
  GameNotification
} from '../types';
import { FLOOR_QUALITY_TYPES, SERVER_TYPES } from '../constants';

interface UseFloorManagementProps {
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  money: number;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  triggerNotification: (message: string, type: GameNotification['type']) => void;
}

interface FloorManagementHook {
  upgradeFloorQuality: (locationId: string, floorId: string, floorQualityId: string) => boolean;
  getFloorCoolingEfficiency: (floorId: string, locationId: string) => number;
}

export const useFloorManagement = ({
  locations,
  setLocations,
  money,
  setMoney,
  triggerNotification
}: UseFloorManagementProps): FloorManagementHook => {
  
  // Function to upgrade floor quality
  const upgradeFloorQuality = useCallback((locationId: string, floorId: string, floorQualityId: string): boolean => {
    if (!locations[locationId] || !locations[locationId].floors || !locations[locationId].floors[floorId]) {
      triggerNotification('Invalid location or floor', 'error');
      return false;
    }
    
    // Find the floor quality type
    const floorQuality = FLOOR_QUALITY_TYPES.find(fq => fq.id === floorQualityId);
    if (!floorQuality) {
      triggerNotification('Invalid floor quality type', 'error');
      return false;
    }
    
    // Check if floor already has this quality or if it's trying to downgrade
    const currentFloorQualityId = locations[locationId].floors[floorId].floorQualityId || 'basic';
    if (currentFloorQualityId === floorQualityId) {
      triggerNotification('Floor already has this quality level', 'info');
      return false;
    }
    
    // Prevent downgrades - floors can only be upgraded to higher qualities
    const qualityLevels: Record<string, number> = { 'basic': 0, 'premium': 1, 'enterprise': 2 };
    if (qualityLevels[currentFloorQualityId] > qualityLevels[floorQualityId]) {
      triggerNotification('Cannot downgrade floor quality. Upgrades are permanent.', 'error');
      return false;
    }
    
    // Check if we have enough money
    if (money < floorQuality.cost) {
      triggerNotification(`Not enough money to upgrade floor. Need $${floorQuality.cost}`, 'error');
      return false;
    }
    
    // Update the floor quality and recalculate cooling usage for all floors
    setLocations(prev => {
      const updatedLocation = { ...prev[locationId] };
      const updatedFloors = { ...updatedLocation.floors };
      updatedFloors[floorId] = { ...updatedFloors[floorId], floorQualityId };
      updatedLocation.floors = updatedFloors;

      // Recalculate location-wide used cooling with new per-floor efficiency
      let totalUsedCooling = 0;
      Object.entries(updatedFloors).forEach(([fid, floor]) => {
        const fqId = fid === floorId ? floorQualityId : (floor.floorQualityId || 'basic');
        const efficiency = FLOOR_QUALITY_TYPES.find(fq => fq.id === fqId)?.coolingBonus ?? 1.0;
        (floor.racks || []).forEach(rack => {
          (rack.servers || []).forEach(server => {
            const st = SERVER_TYPES.find(s => s.id === server.type);
            const raw = st ? st.coolingNeeded : (server.coolingNeeded || 0);
            totalUsedCooling += raw / efficiency;
          });
        });
      });
      updatedLocation.usedCoolingCapacityKW = totalUsedCooling;

      return { ...prev, [locationId]: updatedLocation };
    });
    
    // Deduct the cost
    setMoney(prev => prev - floorQuality.cost);
    
    // Trigger notification
    triggerNotification(`Floor upgraded to ${floorQuality.name}`, 'success');
    
    return true;
  }, [locations, money, setLocations, setMoney, triggerNotification]);
  
  // Function to get floor cooling efficiency based on floor quality
  const getFloorCoolingEfficiency = useCallback((floorId: string, locationId: string): number => {
    if (!locations[locationId] || !locations[locationId].floors || !locations[locationId].floors[floorId]) {
      return 1.0; // Default efficiency if floor doesn't exist
    }
    
    const floorQualityId = locations[locationId].floors[floorId].floorQualityId || 'basic';
    const floorQuality = FLOOR_QUALITY_TYPES.find(fq => fq.id === floorQualityId);
    
    return floorQuality ? floorQuality.coolingBonus : 1.0;
  }, [locations]);
  
  return {
    upgradeFloorQuality,
    getFloorCoolingEfficiency
  };
};
